import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, OnDestroy, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { LoginComponent } from '@auth';
import { LoggedInUserStore } from '@logged-in-user-store';
import { PimsFrontendLoginParametersEndpoint } from '@pims-endpoints';
import { NgUiSrefDirective, ServiceUpgradeInjectorService } from '@shims';
import { Auth, USER_ROLES } from '@types-lib';
import { map, Subscription } from 'rxjs';

interface CmsPimsLoginErrors {
  wrong_credentials: string;
  account_locked: string;
  maintenance: string;
  unauthorized_role: string;
  forbidden: string;
  conflict: string;
  generic: string;
}

@Component({
  selector: 'uk-pims-home',
  standalone: true,
  imports: [LoginComponent, NgUiSrefDirective],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
})
export class HomeComponent implements OnInit, OnDestroy {
  private serviceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  private router = inject(Router);
  private loggedInUserStore = inject(LoggedInUserStore);
  private pimsFrontendLoginParametersEndpoint = new PimsFrontendLoginParametersEndpoint();
  private subscriptions = new Subscription();
  isLoading = signal(false);
  cmsPimsLoginErrors: WritableSignal<CmsPimsLoginErrors | undefined> = signal(undefined);

  errorMessagesText = computed(() => ({
    WRONG_CREDENTIALS:
      this.cmsPimsLoginErrors()?.wrong_credentials || 'Could not login. Verify you are using the correct credentials.',
    ACCOUNT_LOCKED:
      this.cmsPimsLoginErrors()?.account_locked ||
      'For security reasons we\'ve locked your account. To unlock your account please reset your password <a href="/forgot-password">here</a>.',
    MAINTENANCE: this.cmsPimsLoginErrors()?.maintenance || 'Service temporarily unavailable',
    UNAUTHORIZED_ROLE:
      this.cmsPimsLoginErrors()?.unauthorized_role || 'You are not authorized to access this resource.',
    FORBIDDEN: this.cmsPimsLoginErrors()?.forbidden || 'You must have specific roles to use this platform.',
    CONFLICT: this.cmsPimsLoginErrors()?.conflict || 'Conflict error',
    GENERIC: this.cmsPimsLoginErrors()?.generic || 'Sorry, looks like we’ve run into an issue. Please try again later.',
  }));

  additionalLoginErrors: Signal<Auth.AdditionalLoginError[]> = computed(() => [
    () => {
      if (
        this.loggedInUserStore?.user()?.roles?.[USER_ROLES.BUSINESS.ID] ||
        this.loggedInUserStore?.user()?.roles?.[USER_ROLES.DOCTOR.ID] ||
        this.loggedInUserStore?.user()?.roles?.[USER_ROLES.SUPER.ID] ||
        this.loggedInUserStore?.user()?.roles?.[USER_ROLES.QA_USER.ID] ||
        this.loggedInUserStore?.user()?.roles?.[USER_ROLES.SYSTEM.ID]
      ) {
        return null;
      } else {
        this.loggedInUserStore?.logout();
        return new HttpErrorResponse({
          status: 403,
          statusText: 'Forbidden',
          url: '/users/me/details',
          error: {
            message: this.errorMessagesText().UNAUTHORIZED_ROLE,
          },
        });
      }
    },
  ]);

  errorHandlingMap: Signal<Auth.LoginErrorHandlingMap> = computed<Auth.LoginErrorHandlingMap>(
    () =>
      new Map([
        [
          403,
          error => ({
            message: error?.error?.message || this.errorMessagesText().ACCOUNT_LOCKED,
          }),
        ],
        [
          401,
          error => ({
            message: error?.error?.message || this.errorMessagesText().WRONG_CREDENTIALS,
          }),
        ],
        [
          410,
          error => ({
            message: error?.error?.message || this.errorMessagesText().WRONG_CREDENTIALS,
          }),
        ],
        [
          409,
          error => ({
            message: error?.error?.message?.summary || this.errorMessagesText().CONFLICT,
          }),
        ],
        [
          503,
          () => ({
            message: this.errorMessagesText().MAINTENANCE,
          }),
        ],
      ]),
  );

  ngOnInit(): void {
    this.subscriptions.add(this.pimsFrontendLoginParametersEndpoint
      .get()
      .pipe(
        map(pimsParams => ({
          ...pimsParams?.pims?.error_messages,
        })),
      )
      .subscribe((cmsPimsLoginErrors: CmsPimsLoginErrors) => {
        this.cmsPimsLoginErrors.set(cmsPimsLoginErrors);
      }));
  }

  onLoginSuccess(loginResult: Auth.LoginResult) {
    if (loginResult.successful) {
      this.isLoading.set(true);
      this.loggedInUserStore.getAreaCodes({ polling: true });
      this.router.navigate(['visits']).then(() => {
        this.serviceUpgradeInjectorService
          .getAngularJsUiState()
          .go('visits')
          .then(() => this.isLoading.set(false));
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
